import { useState, useEffect, useMemo, Fragment } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Pencil, History, Trash2, Tag, Smartphone, Minus, Upload, Download, FileSpreadsheet, AlertCircle, X, AlertTriangle, ClipboardList } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MOVEMENT_TYPES = {
  sale: { label: 'Venta', color: 'text-red-600' },
  return: { label: 'Devolucion', color: 'text-green-600' },
  shrinkage: { label: 'Merma', color: 'text-orange-600' },
  adjustment: { label: 'Ajuste', color: 'text-blue-600' },
  initial: { label: 'Stock inicial', color: 'text-slate-600' },
  transfer_in: { label: 'Transferencia entrada', color: 'text-green-600' },
  transfer_out: { label: 'Transferencia salida', color: 'text-red-600' },
  repair_use: { label: 'Uso en reparacion', color: 'text-purple-600' },
  repair_return: { label: 'Devolucion reparacion', color: 'text-green-600' },
  stock_edit: { label: 'Ajuste manual', color: 'text-blue-600' },
};

export default function Inventory() {
  const { getAuthHeaders, user, currentBranch } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isMinimosView = location.pathname === '/minimos';
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [models, setModels] = useState([]);
  const [branches, setBranches] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [search, setSearch] = useState('');
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ sku: '', category: 'accessory', modelo: '', name: '', color: '', description: '', provider: '', model_compatibility: '', purchase_price: '', sale_price: '', stock_by_branch: {}, min_stock: '5' });
  const [selectedModels, setSelectedModels] = useState([]);
  // Stock adjustments tracker for edit mode
  const [stockAdjustments, setStockAdjustments] = useState({});
  const [originalStock, setOriginalStock] = useState({});
  // Detail & movements
  const [showDetail, setShowDetail] = useState(false);
  // CSV import/export panel
  const [showCsvPanel, setShowCsvPanel] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvDownloading, setCsvDownloading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [csvError, setCsvError] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [movements, setMovements] = useState([]);
  // Movement form
  const [showMovement, setShowMovement] = useState(false);
  const [movementForm, setMovementForm] = useState({ type: 'adjustment', quantity: '', branch_id: '', note: '' });
  // Category management
  const [showCategories, setShowCategories] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatHasStock, setNewCatHasStock] = useState(true);
  const [editingCat, setEditingCat] = useState(null);
  const [editCatName, setEditCatName] = useState('');
  // Models management
  const [showModels, setShowModels] = useState(false);
  const [newModelName, setNewModelName] = useState('');
  const [editingModel, setEditingModel] = useState(null);
  const [editModelName, setEditModelName] = useState('');

  // --- Mínimos view state ---
  const [minSelected, setMinSelected] = useState({}); // { itemId: bool }
  const [minQty, setMinQty] = useState({}); // { `${itemId}|${branchId}`: number }
  const [minNote, setMinNote] = useState('');
  const [minSaving, setMinSaving] = useState(false);
  const [minTargetBranch, setMinTargetBranch] = useState('');
  const [showOrderConfirm, setShowOrderConfirm] = useState(false);
  const [minSearch, setMinSearch] = useState('');
  // { item_id: 'por_pedir' | 'abierto' | 'en_camino' }
  const [orderStatusMap, setOrderStatusMap] = useState({});

  useEffect(() => { fetchInventory(); fetchCategories(); fetchBranches(); fetchModels(); }, [activeCategory]);

  // Query the DB in real time every time the Mínimos view loads or refreshes.
  // NEVER use cached responses — we bypass HTTP caching with cache-busting query
  // params and no-cache headers, and always rebuild the map from scratch.
  const refreshOrderStatusMap = async () => {
    try {
      const nocache = { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' };
      const t = Date.now();
      const [draftsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/api/purchase-orders/draft?_=${t}`, { headers: { ...getAuthHeaders(), ...nocache } }),
        axios.get(`${API}/api/purchase-orders?_=${t}`, { headers: { ...getAuthHeaders(), ...nocache } }),
      ]);
      const map = {};
      for (const d of (draftsRes.data || [])) {
        if (d.type === 'inv' && d.item_id) map[d.item_id] = 'por_pedir';
      }
      for (const o of (ordersRes.data || [])) {
        const s = o.status === 'draft' ? 'abierto' : o.status;
        if (s !== 'abierto') continue;
        for (const line of (o.items || [])) {
          if (!line.custom && line.item_id) {
            if (map[line.item_id] !== 'en_camino') map[line.item_id] = 'abierto';
          }
        }
      }
      for (const o of (ordersRes.data || [])) {
        if (o.status !== 'en_camino') continue;
        for (const line of (o.items || [])) {
          if (!line.custom && line.item_id) map[line.item_id] = 'en_camino';
        }
      }
      // Full replace — any item that is no longer in drafts/orders drops out and
      // its Status circle goes back to empty.
      setOrderStatusMap(map);
    } catch (err) {
      // If the request fails, still clear the stale map so we never show a
      // status that cannot be verified against the DB.
      console.error(err);
      setOrderStatusMap({});
    }
  };

  useEffect(() => {
    if (!isMinimosView) return;
    refreshOrderStatusMap();
    const onFocus = () => refreshOrderStatusMap();
    // Cover every route back into /minimos: tab focus, tab visibility change,
    // and bfcache restore via pageshow (browser back button).
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('pageshow', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('pageshow', onFocus);
    };
  }, [isMinimosView]);

  // Also refresh whenever the inventory list itself refreshes — this ensures
  // the map is rebuilt from the DB every time the user reloads/searches items.
  useEffect(() => {
    if (!isMinimosView) return;
    refreshOrderStatusMap();
  }, [items, isMinimosView]);

  const fetchInventory = async () => {
    try {
      const params = new URLSearchParams();
      if (activeCategory) params.append('category', activeCategory);
      if (search) params.append('search', search);
      const res = await axios.get(`${API}/api/inventory?${params}`, { headers: getAuthHeaders() });
      setItems(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/api/inventory/categories`, { headers: getAuthHeaders() });
      setCategories([...res.data].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) { console.error(err); }
  };

  const fetchModels = async () => {
    try {
      const res = await axios.get(`${API}/api/settings/compatible_models`, { headers: getAuthHeaders() });
      setModels([...(res.data?.value || [])].sort((a, b) => a.localeCompare(b)));
    } catch {
      setModels([]);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${API}/api/branches`, { headers: getAuthHeaders() });
      setBranches(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchMovements = async (itemId) => {
    try {
      const res = await axios.get(`${API}/api/inventory/${itemId}/movements`, { headers: getAuthHeaders() });
      setMovements(res.data);
    } catch (err) { console.error(err); }
  };

  const openCreate = async () => {
    setEditId(null);
    let suggestedDigits = '0001';
    try {
      const res = await axios.get(`${API}/api/inventory/next-sku`, { headers: getAuthHeaders() });
      suggestedDigits = res.data.digits;
    } catch (err) { console.error(err); }
    setForm({ sku: suggestedDigits, category: activeCategory || 'accessory', modelo: '', name: '', color: '', description: '', provider: '', model_compatibility: '', purchase_price: '', sale_price: '', stock_by_branch: {}, min_stock: '5', min_stock_by_branch: {}, max_stock_by_branch: {}, width_cm: '', height_cm: '', sat_key: '', sat_unit: '' });
    setSelectedModels([]);
    setStockAdjustments({});
    setOriginalStock({});
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditId(item.id);
    const skuDigits = (item.sku || '').startsWith('FX-') ? item.sku.slice(3) : (item.sku || '');
    const itemModels = (item.model_compatibility || '').split(',').map(m => m.trim()).filter(Boolean);
    const stockBranch = item.stock_by_branch || {};
    setForm({
      sku: skuDigits,
      category: item.category || item.type || 'accessory',
      modelo: item.modelo || '',
      name: item.name || '',
      color: item.color || '',
      description: item.description || '',
      provider: item.provider || '',
      model_compatibility: item.model_compatibility || '',
      purchase_price: String(item.purchase_price || ''),
      sale_price: String(item.sale_price || ''),
      stock_by_branch: { ...stockBranch },
      min_stock: String(item.min_stock || 5),
      min_stock_by_branch: item.min_stock_by_branch || {},
      max_stock_by_branch: item.max_stock_by_branch || {},
      width_cm: item.width_cm != null ? String(item.width_cm) : '',
      height_cm: item.height_cm != null ? String(item.height_cm) : '',
      sat_key: item.sat_key || '',
      sat_unit: item.sat_unit || ''
    });
    setSelectedModels(itemModels);
    setOriginalStock({ ...stockBranch });
    setStockAdjustments({});
    setShowForm(true);
  };

  const openDetail = async (item) => {
    setSelectedItem(item);
    setShowDetail(true);
    fetchMovements(item.id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const skuDigits = form.sku.replace(/\D/g, '').slice(0, 4).padStart(4, '0');
    const fullSku = `FX-${skuDigits}`;
    // Calculate final stock_by_branch from adjustments
    const finalStockByBranch = { ...form.stock_by_branch };
    // Apply adjustments to get final values
    for (const [branchId, adj] of Object.entries(stockAdjustments)) {
      const orig = originalStock[branchId] || 0;
      finalStockByBranch[branchId] = orig + adj;
    }
    const payload = {
      ...form,
      sku: fullSku,
      model_compatibility: selectedModels.join(', '),
      purchase_price: parseFloat(form.purchase_price) || 0,
      sale_price: parseFloat(form.sale_price) || 0,
      min_stock: parseInt(form.min_stock) || 5,
      min_stock_by_branch: form.min_stock_by_branch || {},
      max_stock_by_branch: form.max_stock_by_branch || {},
      width_cm: form.width_cm !== '' ? parseFloat(form.width_cm) : null,
      height_cm: form.height_cm !== '' ? parseFloat(form.height_cm) : null,
      sat_key: (form.sat_key || '').trim(),
      sat_unit: (form.sat_unit || '').trim(),
      stock_by_branch: finalStockByBranch
    };
    try {
      if (editId) {
        // Don't send stock_by_branch in PUT when there are adjustments - movements endpoint handles stock
        const editPayload = { ...payload };
        if (Object.keys(stockAdjustments).length > 0) {
          delete editPayload.stock_by_branch;
          delete editPayload.stock;
        }
        await axios.put(`${API}/api/inventory/${editId}`, editPayload, { headers: getAuthHeaders() });
        // Register movements for stock adjustments (this also updates stock)
        for (const [branchId, adj] of Object.entries(stockAdjustments)) {
          if (adj !== 0) {
            await axios.post(`${API}/api/inventory/${editId}/movements`, {
              type: 'stock_edit',
              quantity: adj,
              branch_id: branchId,
              note: `Ajuste manual (${adj > 0 ? '+' : ''}${adj})`
            }, { headers: getAuthHeaders() });
          }
        }
      } else {
        await axios.post(`${API}/api/inventory`, payload, { headers: getAuthHeaders() });
      }
      setShowForm(false);
      fetchInventory();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const adjustStock = (branchId, delta) => {
    const current = stockAdjustments[branchId] || 0;
    const orig = originalStock[branchId] || 0;
    const newAdj = current + delta;
    // Don't allow negative total
    if (orig + newAdj < 0) return;
    setStockAdjustments({ ...stockAdjustments, [branchId]: newAdj });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este articulo?')) return;
    try {
      await axios.delete(`${API}/api/inventory/${id}`, { headers: getAuthHeaders() });
      fetchInventory();
    } catch (err) { console.error(err); }
  };

  const handleMovement = async (e) => {
    e.preventDefault();
    if (!selectedItem) return;
    try {
      await axios.post(`${API}/api/inventory/${selectedItem.id}/movements`, {
        type: movementForm.type,
        quantity: parseInt(movementForm.quantity) || 0,
        branch_id: movementForm.branch_id,
        note: movementForm.note
      }, { headers: getAuthHeaders() });
      setShowMovement(false);
      setMovementForm({ type: 'adjustment', quantity: '', branch_id: '', note: '' });
      fetchMovements(selectedItem.id);
      fetchInventory();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al registrar movimiento');
    }
  };

  // --- Category management ---
  const saveCategories = async (updated) => {
    await axios.put(`${API}/api/inventory/categories`, { categories: updated }, { headers: getAuthHeaders() });
    setCategories(updated);
  };

  const addCategory = async () => {
    if (!newCatName.trim()) return;
    const id = newCatName.trim().toLowerCase().replace(/\s+/g, '_');
    const updated = [...categories, { id, name: newCatName.trim(), has_stock: newCatHasStock }].sort((a, b) => a.name.localeCompare(b.name));
    await saveCategories(updated);
    setNewCatName('');
    setNewCatHasStock(true);
  };

  const removeCategory = async (catId) => {
    await saveCategories(categories.filter(c => c.id !== catId));
  };

  const startEditCategory = (cat) => { setEditingCat(cat.id); setEditCatName(cat.name); };

  const saveEditCategory = async () => {
    if (!editCatName.trim()) return;
    const updated = categories.map(c => c.id === editingCat ? { ...c, name: editCatName.trim() } : c).sort((a, b) => a.name.localeCompare(b.name));
    await saveCategories(updated);
    setEditingCat(null);
    setEditCatName('');
  };

  // --- Models management ---
  const saveModels = async (updated) => {
    await axios.put(`${API}/api/settings/compatible_models`, { value: updated }, { headers: getAuthHeaders() });
    setModels(updated);
  };

  const addModel = async () => {
    if (!newModelName.trim()) return;
    const updated = [...models, newModelName.trim()].sort((a, b) => a.localeCompare(b));
    await saveModels(updated);
    setNewModelName('');
  };

  const removeModel = async (idx) => {
    await saveModels(models.filter((_, i) => i !== idx));
  };

  const startEditModel = (idx) => { setEditingModel(idx); setEditModelName(models[idx]); };

  const saveEditModel = async () => {
    if (!editModelName.trim()) return;
    const updated = models.map((m, i) => i === editingModel ? editModelName.trim() : m).sort((a, b) => a.localeCompare(b));
    await saveModels(updated);
    setEditingModel(null);
    setEditModelName('');
  };

  const toggleModel = (model) => {
    if (selectedModels.includes(model)) {
      setSelectedModels(selectedModels.filter(m => m !== model));
    } else {
      setSelectedModels([...selectedModels, model]);
    }
  };

  const currentCategoryHasStock = () => {
    if (!form.category) return true;
    const cat = categories.find(c => c.id === form.category);
    return cat ? cat.has_stock !== false : true;
  };

  // CSV download — authenticated fetch then trigger browser download.
  const downloadCsv = async () => {
    setCsvError(''); setCsvResult(null); setCsvDownloading(true);
    try {
      const res = await axios.get(`${API}/api/inventory/export.csv?ts=${Date.now()}`, {
        headers: { ...getAuthHeaders(), 'Cache-Control': 'no-cache' },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[:T-]/g, '');
      a.download = `inventario_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setCsvError(err.response?.data?.detail || 'No se pudo descargar el CSV');
    } finally {
      setCsvDownloading(false);
    }
  };

  // CSV upload — multipart/form-data
  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setCsvError(''); setCsvResult(null); setCsvUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/inventory/import-csv`, fd, {
        headers: { ...getAuthHeaders() },
      });
      setCsvResult(res.data);
      // Refresh inventory list silently
      fetchInventory();
    } catch (err) {
      setCsvError(err.response?.data?.detail || 'Error al procesar el CSV');
    } finally {
      setCsvUploading(false);
    }
  };

  // --- Mínimos view derived state ---
  // Items where any branch has stock STRICTLY LESS THAN min_stock (min > 0).
  // Items with stock == min are considered on the boundary and NOT shown.
  const minimosItems = useMemo(() => {
    if (!isMinimosView) return [];
    const q = minSearch.trim().toLowerCase();
    return items.filter(it => {
      const cat = categories.find(c => c.id === (it.category || it.type));
      if (cat?.has_stock === false) return false;
      const sbb = it.stock_by_branch || {};
      const mbb = it.min_stock_by_branch || {};
      const hasLow = branches.some(b => {
        const s = Number(sbb[b.id] || 0);
        const m = Number(mbb[b.id] || 0);
        return m > 0 && s < m;
      });
      if (!hasLow) return false;
      if (!q) return true;
      const catName = (cat?.name || it.category || it.type || '').toLowerCase();
      return (
        (it.sku || '').toLowerCase().includes(q) ||
        (it.name || '').toLowerCase().includes(q) ||
        (it.description || '').toLowerCase().includes(q) ||
        catName.includes(q)
      );
    });
  }, [isMinimosView, items, branches, categories, minSearch]);

  // Default `Cantidad a Pedir` (total, sum across branches) = max(0, Σ max - Σ existencia).
  useEffect(() => {
    if (!isMinimosView) return;
    const initial = {};
    minimosItems.forEach(it => {
      const sbb = it.stock_by_branch || {};
      const xbb = it.max_stock_by_branch || {};
      let sumEx = 0, sumMax = 0;
      branches.forEach(b => {
        sumEx += Number(sbb[b.id] || 0);
        sumMax += Number(xbb[b.id] || 0);
      });
      const suggested = Math.max(0, sumMax - sumEx);
      if (minQty[it.id] === undefined) initial[it.id] = suggested;
    });
    if (Object.keys(initial).length > 0) {
      setMinQty(prev => ({ ...initial, ...prev }));
    }
  }, [isMinimosView, minimosItems, branches]);

  const toggleMinItem = (itemId) => {
    setMinSelected(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const toggleAllMinItems = () => {
    const allSelected = minimosItems.length > 0 && minimosItems.every(it => minSelected[it.id]);
    if (allSelected) {
      setMinSelected({});
    } else {
      const next = {};
      minimosItems.forEach(it => { next[it.id] = true; });
      setMinSelected(next);
    }
  };

  const openGenerateOrder = () => {
    const anySelected = minimosItems.some(it => minSelected[it.id]);
    if (!anySelected) {
      alert('Selecciona al menos un artículo con el checkbox');
      return;
    }
    setMinTargetBranch(currentBranch || (branches[0]?.id || ''));
    setMinNote('');
    setShowOrderConfirm(true);
  };

  const submitOrder = async () => {
    // Push selected items into the 'Artículos por Pedir' cart (draft), NOT create an order.
    const targets = [];
    minimosItems.forEach(it => {
      if (!minSelected[it.id]) return;
      const qty = Number(minQty[it.id] || 0);
      if (qty <= 0) return;
      const sbb = it.stock_by_branch || {};
      const mbb = it.min_stock_by_branch || {};
      const lowBranch = branches.find(b => {
        const s = Number(sbb[b.id] || 0);
        const m = Number(mbb[b.id] || 0);
        return m > 0 && s < m;
      });
      const branchId = lowBranch?.id || currentBranch || branches[0]?.id || '';
      targets.push({ item_id: it.id, branch_id: branchId, cantidad: qty });
    });
    if (targets.length === 0) {
      alert('No hay artículos con cantidad a pedir mayor a 0');
      return;
    }
    setMinSaving(true);
    try {
      for (const t of targets) {
        await axios.post(`${API}/api/purchase-orders/draft`, {
          type: 'inv',
          item_id: t.item_id,
          branch_id: t.branch_id,
          cantidad: t.cantidad,
        }, { headers: getAuthHeaders() });
      }
      setMinSelected({});
      setMinQty({});
      await refreshOrderStatusMap();
      navigate('/purchase-orders');
    } catch (err) {
      alert(err.response?.data?.detail || 'No se pudieron agregar los artículos');
    } finally { setMinSaving(false); }
  };

  // --- Mínimos view render ---
  if (isMinimosView) {
    const anySelected = minimosItems.some(it => minSelected[it.id]);
    const allSelected = minimosItems.length > 0 && minimosItems.every(it => minSelected[it.id]);
    return (
      <div data-testid="minimos-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1D3C6E] font-['Outfit']">Mínimos</h1>
              <p className="text-xs text-slate-500">Artículos por debajo de la cantidad mínima configurada</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-3 text-[10px] text-slate-600" data-testid="minimos-legend">
              <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-400" /> Por Pedir</span>
              <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-500" /> Pedido Abierto</span>
              <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" /> En Camino</span>
            </div>
            <Button
              onClick={submitOrder}
              disabled={!anySelected || minSaving}
              className="bg-[#AFBD20] hover:bg-[#9aa61c] text-[#1D3C6E] font-semibold disabled:opacity-50"
              data-testid="generate-order-btn"
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              {minSaving ? 'Agregando…' : 'Pedir Artículo'}
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="minimos-search"
            className="pl-10"
            placeholder="Buscar por SKU, nombre, descripción o categoría..."
            value={minSearch}
            onChange={(e) => setMinSearch(e.target.value)}
          />
        </div>

        <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th rowSpan={2} className="px-3 py-1 align-bottom" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    className="rounded"
                    checked={allSelected}
                    onChange={toggleAllMinItems}
                    data-testid="min-select-all"
                  />
                </th>
                <th rowSpan={2} className="text-left px-3 py-1 font-medium text-slate-600 align-bottom" style={{ width: '1%', whiteSpace: 'nowrap' }}>SKU</th>
                <th rowSpan={2} className="text-left px-3 py-1 font-medium text-slate-600 align-bottom" style={{ width: '1%', whiteSpace: 'nowrap' }}>Modelo</th>
                <th rowSpan={2} className="text-left px-3 py-1 font-medium text-slate-600 align-bottom">Nombre</th>
                <th rowSpan={2} className="text-left px-3 py-1 font-medium text-slate-600 align-bottom" style={{ width: '1%', whiteSpace: 'nowrap' }}>Color</th>
                <th rowSpan={2} className="text-left px-3 py-1 font-medium text-slate-600 align-bottom">Descripción</th>
                <th rowSpan={2} className="text-left px-3 py-1 font-medium text-slate-600 align-bottom" style={{ width: '1%', whiteSpace: 'nowrap' }}>Categoría</th>
                <th rowSpan={2} className="text-left px-3 py-1 font-medium text-slate-600 align-bottom" style={{ width: '1%', whiteSpace: 'nowrap' }}>Proveedor</th>
                {branches.map((b, i) => (
                  <th key={b.id} colSpan={3}
                    className={`text-center px-2 py-1 font-medium text-slate-700 bg-slate-100 border-l ${i === branches.length - 1 ? 'border-r' : ''} border-slate-200 text-xs`}>
                    {b.name}
                  </th>
                ))}
                <th colSpan={4} className="text-center px-2 py-1 font-bold text-white bg-[#1D3C6E] border-l-2 border-[#AFBD20] text-xs uppercase tracking-wider">
                  Total
                </th>
                <th rowSpan={2} className="text-center px-2 py-1 font-medium text-slate-600 align-bottom border-l border-slate-200 text-[10px] uppercase tracking-wider" style={{ width: '1%', whiteSpace: 'nowrap' }}>Status</th>
              </tr>
              <tr>
                {branches.map((b, i) => (
                  <Fragment key={`h-${b.id}`}>
                    <th className={`text-center px-2 py-1.5 text-[10px] font-medium text-slate-500 uppercase tracking-wider ${i === 0 ? 'border-l' : ''} border-slate-200`} style={{ width: '1%', whiteSpace: 'nowrap' }}>Ex</th>
                    <th className="text-center px-2 py-1.5 text-[10px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '1%', whiteSpace: 'nowrap' }}>Min</th>
                    <th className={`text-center px-2 py-1.5 text-[10px] font-medium text-slate-500 uppercase tracking-wider ${i === branches.length - 1 ? 'border-r' : ''} border-slate-200`} style={{ width: '1%', whiteSpace: 'nowrap' }}>Max</th>
                  </Fragment>
                ))}
                <th className="text-center px-2 py-1.5 text-[10px] font-bold text-[#1D3C6E] uppercase tracking-wider bg-[#AFBD20]/20 border-l-2 border-[#AFBD20]" style={{ width: '1%', whiteSpace: 'nowrap' }}>EX</th>
                <th className="text-center px-2 py-1.5 text-[10px] font-bold text-[#1D3C6E] uppercase tracking-wider bg-[#AFBD20]/20" style={{ width: '1%', whiteSpace: 'nowrap' }}>MIN</th>
                <th className="text-center px-2 py-1.5 text-[10px] font-bold text-[#1D3C6E] uppercase tracking-wider bg-[#AFBD20]/20" style={{ width: '1%', whiteSpace: 'nowrap' }}>MAX</th>
                <th className="text-center px-2 py-1.5 text-[10px] font-bold text-[#1D3C6E] uppercase tracking-wider bg-[#AFBD20]/30 border-r-2 border-[#AFBD20]" style={{ width: '1%', whiteSpace: 'nowrap' }}>Pedir</th>
              </tr>
            </thead>
            <tbody>
              {minimosItems.length === 0 && (
                <tr>
                  <td colSpan={13 + branches.length * 3} className="text-center py-10 text-slate-400">
                    <AlertTriangle className="w-8 h-8 mx-auto mb-1 opacity-40" />
                    No hay artículos por debajo del mínimo
                  </td>
                </tr>
              )}
              {minimosItems.map(it => {
                const sbb = it.stock_by_branch || {};
                const mbb = it.min_stock_by_branch || {};
                const xbb = it.max_stock_by_branch || {};
                const cat = categories.find(c => c.id === (it.category || it.type));
                return (
                  <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-1 align-middle" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                      <input
                        type="checkbox"
                        className="rounded"
                        checked={!!minSelected[it.id]}
                        onChange={() => toggleMinItem(it.id)}
                        data-testid={`min-check-${it.id}`}
                      />
                    </td>
                    <td className="px-3 py-1 font-mono text-xs text-slate-500" style={{ width: '1%', whiteSpace: 'nowrap' }}>{it.sku || '-'}</td>
                    <td className="px-3 py-1 text-xs text-slate-600" style={{ width: '1%', whiteSpace: 'nowrap' }}>{it.modelo || '-'}</td>
                    <td className="px-3 py-1 font-medium text-slate-800">{it.name}</td>
                    <td className="px-3 py-1 text-xs text-slate-600" style={{ width: '1%', whiteSpace: 'nowrap' }}>{it.color || '-'}</td>
                    <td className="px-3 py-1 text-xs text-slate-600" title={it.description || ''}>{it.description || '-'}</td>
                    <td className="px-3 py-1" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                      <span className="status-badge bg-slate-100 text-slate-700">{cat?.name || it.category || it.type || '-'}</span>
                    </td>
                    <td className="px-3 py-1 text-xs text-slate-600" style={{ width: '1%', whiteSpace: 'nowrap' }}>{it.provider || '-'}</td>
                    {branches.map((b, i) => {
                      const s = Number(sbb[b.id] || 0);
                      const m = Number(mbb[b.id] || 0);
                      const x = Number(xbb[b.id] || 0);
                      const isLow = m > 0 && s < m;
                      const borderL = i === 0 ? 'border-l' : '';
                      const borderR = i === branches.length - 1 ? 'border-r' : '';
                      return (
                        <Fragment key={`${it.id}-${b.id}`}>
                          <td className={`px-2 py-1 text-center ${borderL} border-slate-200 ${isLow ? 'bg-red-50' : ''}`} style={{ width: '1%', whiteSpace: 'nowrap' }}>
                            <span className={`text-sm font-semibold ${isLow ? 'text-red-600' : 'text-slate-700'}`}>{s}</span>
                          </td>
                          <td className={`px-2 py-1 text-center ${isLow ? 'bg-red-50' : ''}`} style={{ width: '1%', whiteSpace: 'nowrap' }}>
                            <span className="text-sm text-slate-600">{m || '—'}</span>
                          </td>
                          <td className={`px-2 py-1 text-center ${borderR} border-slate-200 ${isLow ? 'bg-red-50' : ''}`} style={{ width: '1%', whiteSpace: 'nowrap' }}>
                            <span className="text-sm text-slate-600">{x || '—'}</span>
                          </td>
                        </Fragment>
                      );
                    })}
                    {/* Total summary block (aggregated across branches) */}
                    {(() => {
                      let sumEx = 0, sumMin = 0, sumMax = 0;
                      branches.forEach(b => {
                        sumEx += Number(sbb[b.id] || 0);
                        sumMin += Number(mbb[b.id] || 0);
                        sumMax += Number(xbb[b.id] || 0);
                      });
                      const qtyValue = minQty[it.id] ?? Math.max(0, sumMax - sumEx);
                      const isLow = sumMin > 0 && sumEx < sumMin;
                      return (
                        <Fragment>
                          <td className={`px-2 py-1 text-center bg-[#AFBD20]/10 border-l-2 border-[#AFBD20]`} style={{ width: '1%', whiteSpace: 'nowrap' }}>
                            <span className={`text-sm font-bold ${isLow ? 'text-red-600' : 'text-[#1D3C6E]'}`}>{sumEx}</span>
                          </td>
                          <td className="px-2 py-1 text-center bg-[#AFBD20]/10" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                            <span className="text-sm font-semibold text-[#1D3C6E]">{sumMin || '—'}</span>
                          </td>
                          <td className="px-2 py-1 text-center bg-[#AFBD20]/10" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                            <span className="text-sm font-semibold text-[#1D3C6E]">{sumMax || '—'}</span>
                          </td>
                          <td className="px-2 py-1 text-center bg-[#AFBD20]/20 border-r-2 border-[#AFBD20]" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                            <input
                              type="number"
                              min="0"
                              value={qtyValue}
                              onChange={(e) => setMinQty(prev => ({ ...prev, [it.id]: parseInt(e.target.value) || 0 }))}
                              className="w-24 h-9 rounded border-2 border-[#1D3C6E]/30 bg-white text-sm text-center font-bold text-[#1D3C6E] focus:outline-none focus:ring-2 focus:ring-[#AFBD20]/60 focus:border-[#AFBD20]"
                              data-testid={`min-qty-total-${it.id}`}
                            />
                          </td>
                        </Fragment>
                      );
                    })()}
                    {/* Status circle (rightmost) */}
                    <td className="px-3 py-1 text-center" style={{ width: '1%', whiteSpace: 'nowrap' }} data-testid={`min-status-${it.id}`}>
                      {(() => {
                        const st = orderStatusMap[it.id];
                        const cls = st === 'en_camino' ? 'bg-emerald-500' : st === 'abierto' ? 'bg-blue-500' : st === 'por_pedir' ? 'bg-amber-400' : '';
                        const title = st === 'en_camino' ? 'En Camino' : st === 'abierto' ? 'Pedido Abierto' : st === 'por_pedir' ? 'Por Pedir' : '';
                        return st
                          ? <span className={`inline-block w-3 h-3 rounded-full ${cls}`} title={title} data-status={st} />
                          : <span className="inline-block w-3 h-3" />;
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Order confirmation dialog */}
        <Dialog open={showOrderConfirm} onOpenChange={setShowOrderConfirm}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="text-[#1D3C6E] font-['Outfit']">Generar Pedido</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Sucursal destino</label>
                <Select value={minTargetBranch} onValueChange={setMinTargetBranch}>
                  <SelectTrigger data-testid="min-order-branch"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Nota (opcional)</label>
                <textarea
                  className="w-full border border-slate-200 rounded-lg p-2 text-sm min-h-[70px]"
                  value={minNote}
                  onChange={(e) => setMinNote(e.target.value)}
                  placeholder="Proveedor sugerido, urgencia..."
                  data-testid="min-order-note"
                />
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
                Se creará un pedido con <strong>{minimosItems.filter(it => minSelected[it.id]).length}</strong> artículo(s) seleccionado(s).
              </div>
              <Button
                onClick={submitOrder}
                disabled={minSaving}
                className="w-full bg-[#1D3C6E] hover:bg-[#152B50] disabled:opacity-50"
                data-testid="confirm-order-btn"
              >
                {minSaving ? 'Generando...' : 'Confirmar y Generar'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div data-testid="inventory-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#1D3C6E] font-['Outfit']">Inventario</h1>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <Button onClick={() => setShowModels(true)} variant="outline" className="text-sm" data-testid="manage-models-btn">
                <Smartphone className="w-4 h-4 mr-1" /> Modelos
              </Button>
              <Button onClick={() => { setShowCsvPanel(v => !v); setCsvResult(null); setCsvError(''); }} variant="outline" className="text-sm" data-testid="csv-toggle-btn">
                <FileSpreadsheet className="w-4 h-4 mr-1" /> Actualizar Inventario
              </Button>
              <Button onClick={() => setShowCategories(true)} variant="outline" className="text-sm" data-testid="manage-categories-btn">
                <Tag className="w-4 h-4 mr-1" /> Categorias
              </Button>
              <Button onClick={openCreate} className="bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="new-item-button">
                <Plus className="w-4 h-4 mr-2" /> Nuevo Articulo
              </Button>
            </>
          )}
        </div>
      </div>

      {/* CSV import/export panel — inline in main content area */}
      {showCsvPanel && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 mb-4" data-testid="csv-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-[#1D3C6E] font-['Outfit']">Actualizar Inventario</h3>
              <p className="text-xs text-slate-500 mt-0.5">Descarga el inventario, edítalo en Excel y vuelve a subirlo para aplicar los cambios.</p>
            </div>
            <button onClick={() => setShowCsvPanel(false)} className="text-slate-400 hover:text-slate-600" data-testid="csv-close-btn">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Download */}
            <div className="border border-slate-200 rounded-lg p-4 flex flex-col">
              <div className="flex items-center gap-2 mb-1">
                <Download className="w-4 h-4 text-[#1D3C6E]" />
                <h4 className="text-sm font-semibold text-slate-700">Descargar CSV</h4>
              </div>
              <p className="text-xs text-slate-500 mb-3 flex-1">
                Exporta todos los artículos activos con SKU, nombre, descripción, precios, dimensiones, datos SAT, mínimas/máximas y stock por sucursal.
              </p>
              <Button
                onClick={downloadCsv}
                disabled={csvDownloading}
                className="w-full bg-[#1D3C6E] hover:bg-[#152B50] disabled:opacity-60"
                data-testid="csv-download-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                {csvDownloading ? 'Generando...' : 'Descargar CSV'}
              </Button>
            </div>
            {/* Upload */}
            <div className="border border-slate-200 rounded-lg p-4 flex flex-col">
              <div className="flex items-center gap-2 mb-1">
                <Upload className="w-4 h-4 text-[#1D3C6E]" />
                <h4 className="text-sm font-semibold text-slate-700">Subir CSV</h4>
              </div>
              <p className="text-xs text-slate-500 mb-3 flex-1">
                Los artículos se identifican por <strong>SKU</strong>: si existe, se actualizan todos sus campos; si es nuevo, se crea.
              </p>
              <label className="block">
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleCsvUpload}
                  disabled={csvUploading}
                  className="hidden"
                  data-testid="csv-upload-input"
                />
                <span className={`w-full inline-flex items-center justify-center h-10 px-4 rounded-md text-sm font-medium text-white cursor-pointer bg-[#AFBD20] hover:bg-[#9aa61c] transition-colors ${csvUploading ? 'opacity-60 pointer-events-none' : ''}`}>
                  <Upload className="w-4 h-4 mr-1" />
                  {csvUploading ? 'Procesando...' : 'Subir CSV'}
                </span>
              </label>
            </div>
          </div>

          {csvError && (
            <div className="mt-3 flex items-start gap-2 p-2.5 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-700" data-testid="csv-error">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{csvError}</span>
            </div>
          )}

          {csvResult && (
            <div className="mt-3 border border-slate-200 rounded-lg p-3" data-testid="csv-result">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="text-center">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Actualizados</p>
                  <p className="text-2xl font-bold text-emerald-600">{csvResult.updated}</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Creados</p>
                  <p className="text-2xl font-bold text-[#1D3C6E]">{csvResult.created}</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Omitidos por error</p>
                  <p className={`text-2xl font-bold ${csvResult.skipped > 0 ? 'text-rose-600' : 'text-slate-400'}`}>{csvResult.skipped}</p>
                </div>
              </div>
              {csvResult.errors && csvResult.errors.length > 0 && (
                <div className="mt-3 border-t border-slate-100 pt-2">
                  <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1">Errores detectados</p>
                  <div className="max-h-40 overflow-y-auto text-xs space-y-0.5">
                    {csvResult.errors.map((e, i) => (
                      <div key={i} className="flex gap-2 text-rose-700">
                        <span className="font-mono text-slate-400">Fila {e.row}{e.sku ? ` (${e.sku})` : ''}:</span>
                        <span>{e.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Category Filter — Dropdown */}
      <div className="mb-4" data-testid="category-filter">
        <Select value={activeCategory || '__all__'} onValueChange={(v) => setActiveCategory(v === '__all__' ? '' : v)}>
          <SelectTrigger className="h-10 w-full md:w-64" data-testid="category-filter-select">
            <SelectValue placeholder="Filtrar por categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__" data-testid="category-filter-all">Todas</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input data-testid="inventory-search" className="pl-10" placeholder="Buscar por nombre, SKU, modelo..."
          value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchInventory()} />
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th rowSpan={2} className="text-left px-4 py-3 font-medium text-slate-600 align-bottom" style={{ width: '1%', whiteSpace: 'nowrap' }}>SKU</th>
              <th rowSpan={2} className="text-left px-4 py-3 font-medium text-slate-600 align-bottom" style={{ width: '1%', whiteSpace: 'nowrap' }}>Modelo</th>
              <th rowSpan={2} className="text-left px-4 py-3 font-medium text-slate-600 align-bottom">Nombre</th>
              <th rowSpan={2} className="text-left px-4 py-3 font-medium text-slate-600 align-bottom" style={{ width: '1%', whiteSpace: 'nowrap' }}>Color</th>
              <th rowSpan={2} className="text-left px-4 py-3 font-medium text-slate-600 align-bottom">Descripcion</th>
              <th rowSpan={2} className="text-left px-4 py-3 font-medium text-slate-600 align-bottom" style={{ width: '1%', whiteSpace: 'nowrap' }}>Categoria</th>
              <th rowSpan={2} className="text-left px-4 py-3 font-medium text-slate-600 align-bottom" style={{ width: '1%', whiteSpace: 'nowrap' }}>Proveedor</th>
              <th colSpan={Math.max(branches.length, 1)} className="text-center px-3 py-1 font-medium text-slate-600 border-l border-r border-slate-200 bg-slate-100">
                Stock
              </th>
              <th rowSpan={2} className="text-left px-4 py-3 font-medium text-slate-600 align-bottom" style={{ width: '1%', whiteSpace: 'nowrap' }}>Costo</th>
              <th rowSpan={2} className="text-left px-4 py-3 font-medium text-slate-600 align-bottom" style={{ width: '1%', whiteSpace: 'nowrap' }}>Precio</th>
              {isAdmin && <th rowSpan={2} className="text-left px-4 py-3 font-medium text-slate-600 align-bottom" style={{ width: '1%', whiteSpace: 'nowrap' }}>Acciones</th>}
            </tr>
            <tr>
              {branches.map((b, i) => (
                <th
                  key={`th-${b.id}`}
                  className={`text-center px-3 py-1 font-medium text-slate-600 whitespace-nowrap text-xs ${i === 0 ? 'border-l' : ''} ${i === branches.length - 1 ? 'border-r' : ''} border-slate-200`}
                  data-testid={`stock-col-${b.id}`}
                >
                  {b.name}
                </th>
              ))}
              {branches.length === 0 && <th className="text-center px-4 py-2 font-medium text-slate-600 text-xs border-l border-r border-slate-200">—</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const cat = categories.find(c => c.id === (item.category || item.type));
              // Calculate if stock is low using per-branch minimums
              const isLowStock = () => {
                const mbb = item.min_stock_by_branch || {};
                const sbb = item.stock_by_branch || {};
                // If we have per-branch mins, check each branch
                if (Object.keys(mbb).length > 0) {
                  return Object.entries(mbb).some(([bid, minVal]) => (sbb[bid] || 0) < minVal && minVal > 0);
                }
                return false;
              };
              const lowStock = cat?.has_stock !== false && isLowStock();
              return (
                <tr key={item.id} style={{ height: 28 }} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer [&>td]:!py-0" onClick={() => openDetail(item)}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500" style={{ width: '1%', whiteSpace: 'nowrap' }}>{item.sku || '-'}</td>
                  <td className="px-4 py-3 text-xs text-slate-600" style={{ width: '1%', whiteSpace: 'nowrap' }}>{item.modelo || '-'}</td>
                  <td className="px-4 py-3 font-medium">{item.name}</td>
                  <td className="px-4 py-3 text-xs text-slate-600" style={{ width: '1%', whiteSpace: 'nowrap' }}>{item.color || '-'}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-[200px] truncate" title={item.description || ''}>{item.description || '-'}</td>
                  <td className="px-4 py-3" style={{ width: '1%', whiteSpace: 'nowrap' }}><span className="status-badge bg-slate-100 text-slate-700">{cat?.name || item.category || item.type}</span></td>
                  <td className="px-4 py-3 text-xs text-slate-600" style={{ width: '1%', whiteSpace: 'nowrap' }}>{item.provider || '-'}</td>
                  {branches.map(b => {
                    if (cat?.has_stock === false) {
                      return <td key={`cell-${item.id}-${b.id}`} className="px-3 py-3 text-center text-slate-400">N/A</td>;
                    }
                    const branchStock = (item.stock_by_branch || {})[b.id] || 0;
                    const branchMin = (item.min_stock_by_branch || {})[b.id] || 0;
                    const lowBranch = branchMin > 0 && branchStock < branchMin;
                    return (
                      <td key={`cell-${item.id}-${b.id}`} className="px-3 py-3 text-center" data-testid={`stock-cell-${item.id}-${b.id}`}>
                        <span className={`font-medium ${lowBranch ? 'text-red-600' : 'text-slate-700'}`}>
                          {branchStock}
                        </span>
                      </td>
                    );
                  })}
                  {branches.length === 0 && (
                    <td className="px-4 py-3 text-center">
                      {cat?.has_stock !== false ? (
                        <span className={`font-medium ${lowStock ? 'text-red-600' : 'text-slate-700'}`}>
                          {item.stock || 0}
                        </span>
                      ) : <span className="text-slate-400">N/A</span>}
                    </td>
                  )}
                  <td className="px-4 py-3 text-slate-600" style={{ width: '1%', whiteSpace: 'nowrap' }}>${(item.purchase_price || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 font-medium text-[#1D3C6E]" style={{ width: '1%', whiteSpace: 'nowrap' }}>${(item.sale_price || 0).toFixed(2)}</td>
                  {isAdmin && (
                    <td className="px-4 py-3" style={{ width: '1%', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(item)} className="text-[#1D3C6E] hover:text-[#152B50]" data-testid={`edit-item-${item.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(item.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={9 + Math.max(branches.length, 1) + (isAdmin ? 1 : 0)} className="text-center py-8 text-slate-400">No hay articulos</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#1D3C6E] font-['Outfit']">{editId ? 'Editar' : 'Nuevo'} Articulo</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">SKU / Codigo</label>
                <div className="flex items-center">
                  <span className="inline-flex items-center px-3 h-9 rounded-l-lg border border-r-0 border-slate-200 bg-slate-100 text-sm font-mono font-medium text-slate-600">FX-</span>
                  <Input data-testid="item-sku-input" className="rounded-l-none font-mono" value={form.sku}
                    onChange={(e) => setForm({...form, sku: e.target.value.replace(/\D/g, '').slice(0, 4)})}
                    placeholder="0001" maxLength={4} />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Categoria *</label>
                <Select value={form.category} onValueChange={(val) => setForm({...form, category: val})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><label className="text-xs font-medium text-slate-600">Modelo</label><Input data-testid="item-modelo-input" value={form.modelo} onChange={(e) => setForm({...form, modelo: e.target.value})} /></div>
            <div><label className="text-xs font-medium text-slate-600">Nombre *</label><Input data-testid="item-name-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required /></div>
            <div><label className="text-xs font-medium text-slate-600">Color</label><Input data-testid="item-color-input" value={form.color} onChange={(e) => setForm({...form, color: e.target.value})} /></div>
            <div><label className="text-xs font-medium text-slate-600">Descripcion</label><textarea className="w-full border border-slate-200 rounded-lg p-2 text-sm min-h-[50px]" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} /></div>
            <div><label className="text-xs font-medium text-slate-600">Proveedor</label><Input data-testid="item-provider-input" value={form.provider} onChange={(e) => setForm({...form, provider: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-600">Costo</label><Input type="number" step="0.01" value={form.purchase_price} onChange={(e) => setForm({...form, purchase_price: e.target.value})} /></div>
              <div><label className="text-xs font-medium text-slate-600">Precio de venta</label><Input type="number" step="0.01" value={form.sale_price} onChange={(e) => setForm({...form, sale_price: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Ancho (cm)</label>
                <Input data-testid="item-width-cm" type="number" step="0.01" min="0" value={form.width_cm}
                  onChange={(e) => setForm({...form, width_cm: e.target.value})} placeholder="Opcional" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Alto (cm)</label>
                <Input data-testid="item-height-cm" type="number" step="0.01" min="0" value={form.height_cm}
                  onChange={(e) => setForm({...form, height_cm: e.target.value})} placeholder="Opcional" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Clave SAT</label>
                <Input data-testid="item-sat-key" type="text" inputMode="numeric" pattern="[0-9]*" value={form.sat_key}
                  onChange={(e) => setForm({...form, sat_key: e.target.value.replace(/\D/g, '')})} placeholder="Opcional" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Unidad SAT</label>
                <Input data-testid="item-sat-unit" type="text" value={form.sat_unit}
                  onChange={(e) => setForm({...form, sat_unit: e.target.value})} placeholder="Opcional" />
              </div>
            </div>
            {currentCategoryHasStock() && (
              <>
                {branches.length > 0 ? (
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Stock por sucursal</label>
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <table className="w-full text-sm" data-testid="stock-by-branch-table">
                        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                          <tr>
                            <th className="text-left px-3 py-1.5">Sucursal</th>
                            <th className="text-center px-3 py-1.5">Cantidad Existente</th>
                            <th className="text-center px-3 py-1.5">Cantidad Mínima</th>
                            <th className="text-center px-3 py-1.5">Cantidad Máxima</th>
                          </tr>
                        </thead>
                        <tbody>
                          {branches.map(b => {
                            const orig = originalStock[b.id] || 0;
                            const adj = stockAdjustments[b.id] || 0;
                            const currentQty = editId ? orig + adj : (form.stock_by_branch[b.id] || 0);
                            return (
                              <tr key={b.id} className="border-t border-slate-100">
                                <td className="px-3 py-1 font-medium text-slate-700">{b.name}</td>
                                <td className="px-3 py-1">
                                  {editId ? (
                                    <div className="flex items-center justify-center gap-1.5">
                                      <button type="button" onClick={() => adjustStock(b.id, -1)}
                                        className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center hover:bg-red-100 transition-colors border border-red-200" data-testid={`stock-dec-${b.id}`}>
                                        <Minus className="w-3.5 h-3.5 text-red-600" />
                                      </button>
                                      <span className={`min-w-[36px] text-center text-sm font-bold ${adj !== 0 ? 'text-[#1D3C6E]' : 'text-slate-700'}`} data-testid={`stock-qty-${b.id}`}>{currentQty}</span>
                                      <button type="button" onClick={() => adjustStock(b.id, 1)}
                                        className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center hover:bg-green-100 transition-colors border border-green-200" data-testid={`stock-inc-${b.id}`}>
                                        <Plus className="w-3.5 h-3.5 text-green-600" />
                                      </button>
                                      {adj !== 0 && <span className={`text-xs font-medium ml-1 ${adj > 0 ? 'text-green-600' : 'text-red-600'}`}>{adj > 0 ? '+' : ''}{adj}</span>}
                                    </div>
                                  ) : (
                                    <div className="flex justify-center">
                                      <Input type="number" className="h-8 w-24 text-sm text-center"
                                        value={form.stock_by_branch[b.id] || ''}
                                        onChange={(e) => setForm({ ...form, stock_by_branch: { ...form.stock_by_branch, [b.id]: parseInt(e.target.value) || 0 } })}
                                        placeholder="0" />
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-1">
                                  <div className="flex justify-center">
                                    <Input type="number" min="0" className="h-8 w-20 text-sm text-center"
                                      data-testid={`stock-min-${b.id}`}
                                      value={form.min_stock_by_branch?.[b.id] ?? ''}
                                      onChange={(e) => setForm({ ...form, min_stock_by_branch: { ...(form.min_stock_by_branch || {}), [b.id]: parseInt(e.target.value) || 0 } })}
                                      placeholder="0" />
                                  </div>
                                </td>
                                <td className="px-3 py-1">
                                  <div className="flex justify-center">
                                    <Input type="number" min="0" className="h-8 w-20 text-sm text-center"
                                      data-testid={`stock-max-${b.id}`}
                                      value={form.max_stock_by_branch?.[b.id] ?? ''}
                                      onChange={(e) => setForm({ ...form, max_stock_by_branch: { ...(form.max_stock_by_branch || {}), [b.id]: parseInt(e.target.value) || 0 } })}
                                      placeholder="0" />
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Cantidad mínima</label>
                    <Input type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} placeholder="5" />
                    <p className="text-xs text-slate-400 mt-1">Registra sucursales para asignar stock por sucursal</p>
                  </div>
                )}
              </>
            )}
            <Button type="submit" className="w-full bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="submit-item-button">
              {editId ? 'Guardar Cambios' : 'Crear Articulo'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail & Movements Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#1D3C6E] font-['Outfit']">{selectedItem?.name}</DialogTitle></DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">SKU:</span> <span className="font-mono font-medium">{selectedItem.sku || '-'}</span></div>
                <div><span className="text-slate-500">Categoria:</span> <span className="font-medium">{categories.find(c => c.id === (selectedItem.category || selectedItem.type))?.name || selectedItem.category}</span></div>
                <div><span className="text-slate-500">Costo:</span> <span className="font-medium">${(selectedItem.purchase_price || 0).toFixed(2)}</span></div>
                <div><span className="text-slate-500">Precio:</span> <span className="font-medium text-[#1D3C6E]">${(selectedItem.sale_price || 0).toFixed(2)}</span></div>
                <div><span className="text-slate-500">Stock total:</span> <span className="font-bold">{selectedItem.stock || 0}</span></div>
                <div></div>
                {selectedItem.model_compatibility && <div className="col-span-2"><span className="text-slate-500">Modelos compatibles:</span> <span className="font-medium">{selectedItem.model_compatibility}</span></div>}
                {selectedItem.description && <div className="col-span-2"><span className="text-slate-500">Descripcion:</span> <span className="font-medium">{selectedItem.description}</span></div>}
              </div>
              {selectedItem.stock_by_branch && Object.keys(selectedItem.stock_by_branch).length > 0 && (
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <h4 className="text-xs font-semibold text-slate-700 mb-2">Stock por sucursal</h4>
                  <div className="space-y-1">
                    {Object.entries(selectedItem.stock_by_branch).map(([branchId, qty]) => {
                      const minForBranch = selectedItem.min_stock_by_branch?.[branchId] || 0;
                      return (
                        <div key={branchId} className="flex justify-between text-sm">
                          <span className="text-slate-600">{branches.find(b => b.id === branchId)?.name || branchId}</span>
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${minForBranch > 0 && qty < minForBranch ? 'text-red-600' : ''}`}>{qty}</span>
                            {minForBranch > 0 && <span className="text-[10px] text-slate-400">(min: {minForBranch})</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Movements */}
              <div className="border border-slate-200 rounded-lg p-4" data-testid="item-movements-section">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><History className="w-4 h-4 text-[#1D3C6E]" /> Movimientos</h4>
                  {isAdmin && <Button size="sm" variant="outline" onClick={() => setShowMovement(true)} className="text-xs" data-testid="add-movement-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Registrar</Button>}
                </div>
                {movements.length > 0 ? (
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {movements.map(m => (
                      <div key={m.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded text-xs">
                        <div className="flex-1">
                          <span className={`font-medium ${MOVEMENT_TYPES[m.type]?.color || 'text-slate-600'}`}>{MOVEMENT_TYPES[m.type]?.label || m.type}</span>
                          {m.note && <span className="text-slate-500 ml-2">- {m.note}</span>}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`font-bold ${m.quantity >= 0 ? 'text-green-600' : 'text-red-600'}`}>{m.quantity >= 0 ? '+' : ''}{m.quantity}</span>
                          <span className="text-slate-400 text-[10px] w-28 text-right">{new Date(m.created_at).toLocaleString('es-MX')}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-slate-400 text-center py-4">Sin movimientos registrados</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Movement Dialog */}
      <Dialog open={showMovement} onOpenChange={setShowMovement}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-[#1D3C6E] font-['Outfit']">Registrar Movimiento</DialogTitle></DialogHeader>
          <form onSubmit={handleMovement} className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Tipo *</label>
              <Select value={movementForm.type} onValueChange={(val) => setMovementForm({...movementForm, type: val})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="adjustment">Ajuste</SelectItem>
                  <SelectItem value="return">Devolucion</SelectItem>
                  <SelectItem value="shrinkage">Merma</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Cantidad * <span className="text-slate-400">(positivo=entrada, negativo=salida)</span></label>
              <Input type="number" value={movementForm.quantity} onChange={(e) => setMovementForm({...movementForm, quantity: e.target.value})} required placeholder="Ej: 5 o -3" />
            </div>
            {branches.length > 0 && (
              <div>
                <label className="text-xs font-medium text-slate-600">Sucursal</label>
                <Select value={movementForm.branch_id} onValueChange={(val) => setMovementForm({...movementForm, branch_id: val})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><label className="text-xs font-medium text-slate-600">Nota</label><Input value={movementForm.note} onChange={(e) => setMovementForm({...movementForm, note: e.target.value})} placeholder="Motivo del movimiento" /></div>
            <Button type="submit" className="w-full bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="submit-movement-btn">Registrar</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Management Dialog */}
      <Dialog open={showCategories} onOpenChange={setShowCategories}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-[#1D3C6E] font-['Outfit']">Administrar Categorias</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {categories.map(cat => (
                <div key={cat.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                  {editingCat === cat.id ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input className="h-7 text-sm" value={editCatName} onChange={(e) => setEditCatName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && saveEditCategory()} />
                      <Button size="sm" onClick={saveEditCategory} className="h-7 text-xs bg-[#1D3C6E]">OK</Button>
                      <button onClick={() => setEditingCat(null)} className="text-xs text-slate-400">Cancelar</button>
                    </div>
                  ) : (
                    <>
                      <div>
                        <span className="text-sm font-medium">{cat.name}</span>
                        <span className="text-xs text-slate-400 ml-2">{cat.has_stock !== false ? 'Con stock' : 'Sin stock'}</span>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => startEditCategory(cat)} className="text-xs text-[#1D3C6E] hover:underline">Editar</button>
                        <button onClick={() => removeCategory(cat.id)} className="text-xs text-red-500 hover:text-red-700">Eliminar</button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="border-t border-slate-200 pt-3">
              <label className="text-xs font-medium text-slate-600 mb-1 block">Nueva categoria</label>
              <div className="flex gap-2 mb-2">
                <Input placeholder="Nombre..." value={newCatName} onChange={(e) => setNewCatName(e.target.value)} className="text-sm" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())} />
                <Button onClick={addCategory} size="sm" className="bg-[#1D3C6E] shrink-0" data-testid="add-category-btn">Agregar</Button>
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={newCatHasStock} onChange={(e) => setNewCatHasStock(e.target.checked)} className="rounded" />
                Maneja stock (cantidad por sucursal)
              </label>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Models Management Dialog */}
      <Dialog open={showModels} onOpenChange={setShowModels}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-[#1D3C6E] font-['Outfit']">Administrar Modelos Compatibles</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {models.map((model, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                  {editingModel === idx ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input className="h-7 text-sm" value={editModelName} onChange={(e) => setEditModelName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && saveEditModel()} />
                      <Button size="sm" onClick={saveEditModel} className="h-7 text-xs bg-[#1D3C6E]">OK</Button>
                      <button onClick={() => setEditingModel(null)} className="text-xs text-slate-400">Cancelar</button>
                    </div>
                  ) : (
                    <>
                      <span className="text-sm font-medium">{model}</span>
                      <div className="flex gap-2">
                        <button onClick={() => startEditModel(idx)} className="text-xs text-[#1D3C6E] hover:underline">Editar</button>
                        <button onClick={() => removeModel(idx)} className="text-xs text-red-500 hover:text-red-700">Eliminar</button>
                      </div>
                    </>
                  )}
                </div>
              ))}
              {models.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No hay modelos configurados</p>}
            </div>
            <div className="border-t border-slate-200 pt-3">
              <label className="text-xs font-medium text-slate-600 mb-1 block">Nuevo modelo</label>
              <div className="flex gap-2">
                <Input placeholder="Ej: iPhone 16 Pro, Samsung S24..." value={newModelName} onChange={(e) => setNewModelName(e.target.value)} className="text-sm" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addModel())} />
                <Button onClick={addModel} size="sm" className="bg-[#1D3C6E] shrink-0" data-testid="add-model-btn">Agregar</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
